import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  useTableDetail,
  useUpdateTableReview,
  useUpdateColumnReview,
  useDeleteEdge,
} from '../../../hooks/useDbDictionary.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { EnumDetailPopup } from './EnumDetailPopup.js';
import { AddEdgePopup } from './AddEdgePopup.js';
import { EdgeTag } from './EdgeTag.js';
import { ColumnRow } from './ColumnRow.js';
import { RelatedTablesSection } from './RelatedTablesSection.js';

interface TableDetailModalProps {
  /** 表示するスキーマ名 */
  schema: string;
  /** 表示するテーブル名 */
  tableName: string;
  /** モーダルを閉じるコールバック */
  onClose: () => void;
}

/**
 * テーブル詳細モーダル
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business テーブル詳細モーダル。カラム一覧・AI生成説明・人間レビュー注釈の編集、死にカラム/死にテーブル判定トグル、checked状態管理を提供
 * @graph-connects useTableDetail [calls] テーブル・カラムの詳細データを取得
 * @graph-connects useUpdateTableReview [calls] テーブル説明・isDead・isChecked状態を更新
 * @graph-connects useUpdateColumnReview [calls] カラム説明・isDead状態を更新
 */
export function TableDetailModal({
  schema: initialSchema,
  tableName: initialTableName,
  onClose,
}: TableDetailModalProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');

  // モーダル内で関連テーブルへ遷移できるよう、表示中のテーブルを内部状態で持つ。
  // 親から渡される schema/tableName が変わった場合（一覧から別テーブルを開いた等）は同期する。
  const [current, setCurrent] = useState({ schema: initialSchema, table: initialTableName });
  useEffect(() => {
    setCurrent({ schema: initialSchema, table: initialTableName });
  }, [initialSchema, initialTableName]);
  const schema = current.schema;
  const tableName = current.table;
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useTableDetail(schema, tableName);
  const updateTableReview = useUpdateTableReview();
  const updateColumnReview = useUpdateColumnReview();

  // 編集状態
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingTable, setEditingTable] = useState(false);
  const [tableEditValue, setTableEditValue] = useState('');
  // エッジ操作の popup 状態
  const deleteEdge = useDeleteEdge(schema, tableName);
  const [enumPopup, setEnumPopup] = useState<string | null>(null);
  const [addEdgePopup, setAddEdgePopup] = useState<{
    defaultEdgeType: 'REFERENCES' | 'SAME_ENTITY' | 'USES_ENUM';
    sourceColumn?: string;
  } | null>(null);

  // ESCキーで閉じる
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  /** テーブルを確認済みにする */
  const handleMarkChecked = () => {
    updateTableReview.mutate({ schema, table: tableName, data: { isChecked: true } });
  };

  /** テーブルの確認済みを解除する */
  const handleUnmarkChecked = () => {
    updateTableReview.mutate({ schema, table: tableName, data: { isChecked: false } });
  };

  /** テーブル説明を保存 */
  const handleSaveTableDesc = () => {
    updateTableReview.mutate(
      { schema, table: tableName, data: { humanDescription: tableEditValue } },
      {
        onSuccess: () => {
          setEditingTable(false);
        },
      },
    );
  };

  /** カラム説明を保存 */
  const handleSaveColumnDesc = (colName: string) => {
    updateColumnReview.mutate(
      { schema, table: tableName, column: colName, data: { humanDescription: editValue } },
      {
        onSuccess: () => {
          setEditingColumn(null);
        },
      },
    );
  };

  /** テーブルの廃止状態を切り替え */
  const handleToggleTableDead = () => {
    if (!data) return;
    updateTableReview.mutate({
      schema,
      table: tableName,
      data: { isDead: !data.table.isDead },
    });
  };

  /** カラムの廃止状態を切り替え */
  const handleToggleColumnDead = (colName: string, currentIsDead: boolean) => {
    updateColumnReview.mutate({
      schema,
      table: tableName,
      column: colName,
      data: { isDead: !currentIsDead },
    });
  };

  // 更新系ミューテーションのうち「最後に実行した操作」の結果だけを表示する。
  // TanStack Query は各ミューテーションの error を次の実行 / reset まで保持するため、
  // 単純な ?? チェーンだと固定優先順位になり、かつ成功した別操作の後も古いエラーが
  // 残留してしまう。submittedAt が最大 = 直近に実行したミューテーションを選び、それが
  // 失敗しているときだけエラーを表示する（直近の操作が成功していれば古いエラーは出さない）。
  // 失敗しても画面が無反応に見えないよう、テーブル説明欄の上にメッセージとして表示する。
  const latestMutation = [deleteEdge, updateColumnReview, updateTableReview].reduce(
    (latest, mutation) => (mutation.submittedAt > latest.submittedAt ? mutation : latest),
  );
  const mutationError = latestMutation.error;

  /** 関連テーブルをクリックしたとき、そのテーブルの詳細へ切り替える */
  const handleNavigateTable = (nextSchema: string, nextTable: string) => {
    setCurrent({ schema: nextSchema, table: nextTable });
    // 編集中・popup 状態をリセットして上部へスクロール
    setEditingColumn(null);
    setEditValue('');
    setEditingTable(false);
    setTableEditValue('');
    setEnumPopup(null);
    setAddEdgePopup(null);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-start justify-center pt-[4.5rem] pb-8"
      onClick={onClose}
    >
      {/* オーバーレイ */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* モーダル本体 */}
      <div
        ref={scrollRef}
        className="relative w-full max-w-4xl mx-4 max-h-[calc(100vh-4rem)] overflow-y-auto bg-[var(--color-bg-surface)] rounded-xl border border-[var(--color-border)] shadow-2xl animate-fade-in"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* ヘッダー */}
        <div className="sticky top-0 z-10 bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-[var(--color-text-primary)] font-mono">
              {schema}.{tableName}
            </h2>
            {data?.table.isDead && (
              <Badge variant="error" size="sm">
                {t('dbDictionary.table.dead')}
              </Badge>
            )}
            {data?.table.isChecked && (
              <Badge variant="success" size="sm">
                {t('dbDictionary.table.checked')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && data && (
              <Button
                variant={data.table.isDead ? 'secondary' : 'danger'}
                size="sm"
                loading={updateTableReview.isPending}
                onClick={handleToggleTableDead}
              >
                {data.table.isDead
                  ? t('dbDictionary.table.alive')
                  : t('dbDictionary.table.markAsDead')}
              </Button>
            )}
            {canEdit && data && !data.table.isChecked && (
              <Button
                variant="primary"
                size="sm"
                loading={updateTableReview.isPending}
                onClick={handleMarkChecked}
              >
                {t('dbDictionary.table.markAsChecked')}
              </Button>
            )}
            {canEdit && data && data.table.isChecked && (
              <Button
                variant="secondary"
                size="sm"
                loading={updateTableReview.isPending}
                onClick={handleUnmarkChecked}
              >
                {t('dbDictionary.table.unmarkAsChecked')}
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5 text-[var(--color-text-muted)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="px-6 py-4">
          {isLoading && (
            <div className="space-y-6 animate-fade-in">
              {/* テーブル説明スケルトン */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-4 w-16 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-5 w-20 rounded-full bg-[var(--color-bg-elevated)] animate-pulse" />
                </div>
                <div className="h-20 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
              </div>
              {/* カラム一覧スケルトン */}
              <div>
                <div className="h-4 w-24 rounded bg-[var(--color-bg-elevated)] animate-pulse mb-3" />
                <div className="space-y-1">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-4 w-28 rounded bg-[var(--color-bg-base)] animate-pulse" />
                        <div className="h-4 w-20 rounded-full bg-[var(--color-bg-base)] animate-pulse" />
                      </div>
                      <div className="h-3 w-3/4 rounded bg-[var(--color-bg-base)] animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isError && (
            <div className="text-center py-12 text-[var(--color-text-muted)]">
              {t('feedback.errorMessage')}
            </div>
          )}

          {data && (
            <>
              {/* 更新系ミューテーションのエラー表示。失敗しても画面が無反応に見えないようにする */}
              {mutationError && (
                <div className="mb-4 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/10 px-3 py-2 text-sm text-[var(--color-error)]">
                  {mutationError instanceof Error
                    ? mutationError.message
                    : t('feedback.errorMessage')}
                </div>
              )}

              {/* テーブル説明 */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                    {t('dbDictionary.table.description')}
                  </h3>
                  <Badge variant="info" size="sm">
                    {t('dbDictionary.table.generatedDescription')}
                  </Badge>
                </div>
                {canEdit && editingTable ? (
                  <div className="space-y-2">
                    <textarea
                      value={tableEditValue}
                      onChange={(e) => {
                        setTableEditValue(e.target.value);
                      }}
                      rows={3}
                      className="w-full rounded-lg border border-[var(--color-border-focus)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none resize-y"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        loading={updateTableReview.isPending}
                        onClick={handleSaveTableDesc}
                      >
                        {t('dbDictionary.actions.save')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingTable(false);
                        }}
                      >
                        {t('dbDictionary.actions.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-sm text-[var(--color-text-primary)] whitespace-pre-wrap rounded-lg bg-[var(--color-bg-elevated)] p-3 border border-[var(--color-border)] transition-colors ${canEdit ? 'cursor-pointer hover:border-[var(--color-border-hover)]' : ''}`}
                    onClick={
                      canEdit
                        ? () => {
                            setTableEditValue(
                              data.table.humanDescription ?? data.table.description ?? '',
                            );
                            setEditingTable(true);
                          }
                        : undefined
                    }
                  >
                    {data.table.humanDescription ?? data.table.description ?? '-'}
                  </div>
                )}
              </div>

              {/* SAME_ENTITY (テーブルレベル) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                    Same Entity
                  </h3>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddEdgePopup({ defaultEdgeType: 'SAME_ENTITY' });
                      }}
                    >
                      + 追加
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.sameEntities.length === 0 ? (
                    <p className="text-xs text-[var(--color-text-muted)]">登録なし</p>
                  ) : (
                    data.sameEntities.map((se) => (
                      <EdgeTag
                        key={se.edgeId}
                        variant="same"
                        label={se.linkedTable + (se.syncMethod ? ` (${se.syncMethod})` : '')}
                        canEdit={canEdit}
                        isDeleting={deleteEdge.isPending}
                        onDelete={() => {
                          deleteEdge.mutate(se.edgeId);
                        }}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* テーブルレベル FK 追加ボタン (FK は列単位で出るが、テーブル全体に対する追加導線として用意) */}
              {canEdit && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                      エッジ追加
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddEdgePopup({ defaultEdgeType: 'REFERENCES' });
                      }}
                    >
                      + FK
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddEdgePopup({ defaultEdgeType: 'USES_ENUM' });
                      }}
                    >
                      + USES_ENUM
                    </Button>
                  </div>
                </div>
              )}

              {/* USES_SHARED_SERVICE */}
              {data.sharedServices && data.sharedServices.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                    {t('dbDictionary.edges.sharedService')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {data.sharedServices.map((ss) => (
                      <span
                        key={ss.serviceName}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm border border-emerald-500/20"
                      >
                        {ss.serviceName}
                        {ss.serviceLevel && (
                          <span className="text-xs text-[var(--color-text-muted)]">
                            ({ss.serviceLevel})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* カラム一覧 */}
              <div>
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">
                  {t('dbDictionary.table.columns')} ({data.columns.length})
                </h3>
                <div className="space-y-1">
                  {data.columns.map((col) => (
                    <ColumnRow
                      key={col.qualifiedName}
                      col={col}
                      canEdit={canEdit}
                      editingColumn={editingColumn}
                      editValue={editValue}
                      setEditValue={setEditValue}
                      setEditingColumn={setEditingColumn}
                      setEnumPopup={setEnumPopup}
                      setAddEdgePopup={setAddEdgePopup}
                      updateColumnReview={updateColumnReview}
                      deleteEdge={deleteEdge}
                      handleSaveColumnDesc={handleSaveColumnDesc}
                      handleToggleColumnDead={handleToggleColumnDead}
                      t={t}
                    />
                  ))}
                </div>
              </div>

              {/* 関連テーブル (エッジで繋がるテーブルを edgeType 別に表示。クリックで遷移) */}
              <RelatedTablesSection
                relatedTables={data.relatedTables ?? []}
                onNavigate={handleNavigateTable}
                t={t}
              />
            </>
          )}
        </div>
        {/* Enum 詳細 popup */}
        {enumPopup && (
          <EnumDetailPopup
            enumName={enumPopup}
            parentTable={{ schema, table: tableName }}
            onClose={() => {
              setEnumPopup(null);
            }}
          />
        )}
        {/* エッジ追加 popup */}
        {addEdgePopup && (
          <AddEdgePopup
            parentTable={{ schema, table: tableName }}
            defaultEdgeType={addEdgePopup.defaultEdgeType}
            sourceColumn={addEdgePopup.sourceColumn}
            onClose={() => {
              setAddEdgePopup(null);
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
